import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const PREF_LABELS = {
  assignment: "Task assignment notifications",
  reassignment: "Task reassignment notifications",
  due_reminder: "Due-date reminders",
  overdue: "Overdue notifications",
  review: "Task review notifications",
  completion: "Task completion / approval notifications",
};

function ToggleRow({ label, desc, checked, onChange, disabled, testid }) {
  return (
    <div className={`flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3.5 ${disabled ? "opacity-60" : ""}`}>
      <div>
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        {desc && <div className="text-xs text-slate-500">{desc}</div>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

export default function Settings() {
  const { user, setUser } = useAuth();
  const isAdmin = user.role === "owner" || user.role === "admin";
  const [settings, setSettings] = useState(null);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    setPrefs(user.notification_prefs || Object.fromEntries(Object.keys(PREF_LABELS).map((k) => [k, true])));
  }, [user]);

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await api.put("/settings", {
        allow_same_level_assignment: next.allow_same_level_assignment,
        whatsapp_enabled: next.whatsapp_enabled,
        email_enabled: next.email_enabled,
        allow_user_opt_out: next.allow_user_opt_out,
        reminder_days_before: next.reminder_days_before,
      });
      toast.success("Company settings saved");
    } catch (e) {
      toast.error(e.friendlyMessage);
    }
  };

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      await api.put("/users/me/notification-prefs", next);
      setUser({ ...user, notification_prefs: next });
      toast.success("Notification preferences saved");
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  if (!settings || !prefs) return <div className="flex h-64 items-center justify-center text-slate-400">Loading settings...</div>;

  return (
    <div className="mx-auto max-w-3xl animate-fade-in space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Configuration</div>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Settings</h1>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-1 flex items-center gap-2">
          <h3 className="font-heading text-lg font-bold text-slate-900">Assignment Permissions</h3>
          {!isAdmin && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Read only - Owner/Admin only</span>}
        </div>
        <p className="mb-4 text-sm text-slate-500">Controls who can assign tasks to whom, based on hierarchy.</p>
        <ToggleRow
          testid="settings-same-level-toggle"
          label="Allow Same-Level Task Assignment"
          desc={settings.allow_same_level_assignment
            ? "ON - Users can assign tasks to peers at the same hierarchy level (e.g. Manager to Manager)."
            : "OFF - Users can only assign tasks to people below them in the hierarchy."}
          checked={settings.allow_same_level_assignment}
          disabled={!isAdmin}
          onChange={(v) => saveSettings({ allow_same_level_assignment: v })}
        />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-notification-channels">
        <div className="mb-1 flex items-center gap-2">
          <h3 className="font-heading text-lg font-bold text-slate-900">Company Notification Channels</h3>
          {!isAdmin && <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Read only</span>}
        </div>
        <p className="mb-4 text-sm text-slate-500">Enable or disable outbound channels company-wide.</p>
        <div className="space-y-3">
          <ToggleRow testid="settings-whatsapp-toggle" label="WhatsApp notifications"
            desc="Assignment alerts via WhatsApp provider (currently in mock mode until a provider is configured)."
            checked={settings.whatsapp_enabled} disabled={!isAdmin}
            onChange={(v) => saveSettings({ whatsapp_enabled: v })} />
          <ToggleRow testid="settings-email-toggle" label="Email notifications"
            desc="Assignment alerts via the configured email service."
            checked={settings.email_enabled} disabled={!isAdmin}
            onChange={(v) => saveSettings({ email_enabled: v })} />
          <ToggleRow testid="settings-optout-toggle" label="Allow users to opt out"
            desc="When off, individual notification preferences below are ignored and all notifications are sent."
            checked={settings.allow_user_opt_out} disabled={!isAdmin}
            onChange={(v) => saveSettings({ allow_user_opt_out: v })} />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="settings-my-preferences">
        <h3 className="font-heading text-lg font-bold text-slate-900">My Notification Preferences</h3>
        <p className="mb-4 text-sm text-slate-500">
          Choose which events notify you. The task creator's selection at assignment time controls assignment alerts.
        </p>
        <div className="space-y-3">
          {Object.entries(PREF_LABELS).map(([key, label]) => (
            <ToggleRow key={key} testid={`settings-pref-${key}`} label={label}
              checked={!!prefs[key]} onChange={(v) => savePrefs({ [key]: v })} />
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
          <Save className="h-3.5 w-3.5" /> Preferences save automatically when toggled.
        </div>
      </div>
    </div>
  );
}
